import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise.js';
import { useTexture } from '@react-three/drei';
import { makeBillboardGeometry, TREE_KINDS } from './treeBillboards';

// The world outside the glass hall, seen through its windows (and the glass roof):
// rolling meadows, a lake, a few hundred trees, and snow-capped mountains on the horizon.
// It follows the climate: trees sway in the wind, the ground darkens when wet, snow settles on
// upward-facing surfaces, fog swallows the distance (scene fog), and night/lightning light it.

const IS_MOBILE = typeof window !== 'undefined' && (window.innerWidth < 768 || /iPhone|iPad|Android/i.test(navigator.userAgent));
export const LAKE = { x: -95, z: 75, r: 38 };

const noise = new SimplexNoise({ random: (() => { let s = 42; return () => ((s = (s * 16807) % 2147483647) / 2147483647); })() });
const fbm = (x, z, oct = 5) => {
  let v = 0, a = 0.5, f = 1;
  for (let i = 0; i < oct; i++) { v += a * noise.noise(x * f, z * f); f *= 2.03; a *= 0.5; }
  return v;
};
const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

// Ridged multifractal noise: sharp crests and valleys, like real eroded mountain ranges.
const ridged = (x, z, oct = 6) => {
  let v = 0, a = 0.5, f = 1, weight = 1;
  for (let i = 0; i < oct; i++) {
    let n = 1 - Math.abs(noise.noise(x * f, z * f));
    n *= n * weight;
    weight = Math.min(1, n * 1.6);
    v += n * a;
    f *= 2.1; a *= 0.5;
  }
  return v;
};

// Ground height (metres) at a point. Flat lawn around the hall, meadows and hills further out,
// a lake basin, and a ring of real-looking ridged mountains on the horizon.
export const groundHeight = (x, z) => {
  const r = Math.hypot(x, z);
  let h = smooth(26, 75, r) * (fbm(x / 90, z / 90) * 14 + 7) + smooth(24, 45, r) * fbm(x / 22, z / 22, 3) * 1.4;
  const m = smooth(250, 365, r);
  if (m > 0) {
    // domain-warp the coordinates so ranges bend naturally instead of looking regular
    const wx = x + 40 * fbm(x / 160 + 11, z / 160, 3), wz = z + 40 * fbm(x / 160, z / 160 + 17, 3);
    const massif = 0.55 + 0.45 * fbm(x / 260 + 5, z / 260 - 2, 3);   // some peaks taller than others
    h += m * (25 + 175 * massif * Math.pow(ridged(wx / 170, wz / 170), 1.35));
  }
  const dl = Math.hypot(x - LAKE.x, z - LAKE.z);
  h = h * smooth(LAKE.r * 0.7, LAKE.r * 1.6, dl) - 2.2 * (1 - smooth(LAKE.r * 0.6, LAKE.r * 1.05, dl));
  return h - 0.05;
};

// Shared shader tweaks: snow on upward-facing surfaces, wet darkening, and wind sway for trees.
const weatherUniforms = () => ({ uSnow: { value: 0 }, uWet: { value: 0 }, uWind: { value: 0 }, uWindDir: { value: new THREE.Vector2(1, 0) }, uTime: { value: 0 } });
const addWeather = (material, uniforms, { sway = false } = {}) => {
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uWind;\nuniform vec2 uWindDir;\nuniform float uTime;\nvarying float vUp;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vUp = normalize(mat3(modelMatrix) * objectNormal).y;
        ${sway ? `#ifdef USE_INSTANCING
          float bend = max(transformed.y, 0.0) * max(transformed.y, 0.0) * 0.0035;
          float phase = instanceMatrix[3].x * 0.37 + instanceMatrix[3].z * 0.23;
          float gust = sin(uTime * 1.6 + phase) + 0.45 * sin(uTime * 3.7 + phase * 1.7);
          // lean downwind and sway in gusts, in world space (each tree is rotated differently),
          // with a little side-to-side motion across the wind
          float along = (0.15 + uWind * 1.3) * (0.35 * uWind + gust);
          float across = (0.08 + uWind * 0.6) * cos(uTime * 1.3 + phase) * 0.6;
          vec3 worldSway = vec3(uWindDir.x * along - uWindDir.y * across, 0.0, uWindDir.y * along + uWindDir.x * across) * bend;
          transformed += inverse(mat3(instanceMatrix)) * worldSway;
        #endif` : ''}`
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uSnow;\nuniform float uWet;\nvarying float vUp;')
      // Tree billboards are double-sided: light both faces with the same outward "crown" normal,
      // otherwise the back of each crossed quad turns almost black.
      .replace('#include <normal_fragment_begin>', sway ? '#include <normal_fragment_begin>\n#ifdef DOUBLE_SIDED\nnormal *= faceDirection;\n#endif' : '#include <normal_fragment_begin>')
      // wet ground glistens: less rough while it's wet (terrain only)
      .replace('#include <roughnessmap_fragment>', sway ? '#include <roughnessmap_fragment>' : '#include <roughnessmap_fragment>\nroughnessFactor *= 1.0 - 0.45 * uWet;')
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        diffuseColor.rgb *= 1.0 - 0.28 * uWet;
        ${sway
          ? `// trees: snow sits on the brighter outer foliage; the dark inner crown stays green
             float leafLight = dot(diffuseColor.rgb, vec3(0.3, 0.59, 0.11));
             diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.93, 0.95, 0.98), uSnow * smoothstep(0.35, 0.8, vUp) * clamp(0.25 + leafLight * 2.2, 0.0, 0.8));`
          : 'diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.93, 0.95, 0.98), uSnow * smoothstep(0.35, 0.8, vUp));'}`
      );
  };
  material.customProgramCacheKey = () => (sway ? 'landscape-sway' : 'landscape');
  return material;
};

// Fine grass/soil detail (grey values around 1, multiplied with the terrain colour), tiled every 7 m.
const makeGroundDetailTexture = () => {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = 'rgb(225,225,225)';
  ctx.fillRect(0, 0, size, size);
  let seed = 19;
  const r = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 9000; i++) {                // grass blades, wrapped so the texture tiles
    const x = r() * size, y = r() * size, len = 3 + r() * 7, a = -Math.PI / 2 + (r() - 0.5) * 0.9;
    const v = 150 + Math.floor(r() * 105);
    ctx.strokeStyle = `rgb(${v},${Math.min(255, v + 12)},${v - 10})`;
    ctx.lineWidth = 1;
    for (const dx of [0, -size, size]) for (const dy of [0, -size, size]) {
      ctx.beginPath(); ctx.moveTo(x + dx, y + dy); ctx.lineTo(x + dx + Math.cos(a) * len, y + dy + Math.sin(a) * len); ctx.stroke();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
};

// Terrain on a polar grid: fine detail where it matters (the mountain ring), coarse nearby,
// coloured by height and slope (meadow, forest, rock strata, snow on gentle high ground).
const R_MAX = 450;
const Terrain = ({ uniforms }) => {
  const geometry = useMemo(() => {
    const A = IS_MOBILE ? 260 : 460;   // segments around
    const RINGS = IS_MOBILE ? 90 : 150;
    const radiusAt = (k) => R_MAX * Math.pow(k / RINGS, 1.25); // rings get denser farther out? (slightly)
    const verts = [];
    for (let k = 0; k <= RINGS; k++) {
      const rr = radiusAt(k);
      for (let a = 0; a < A; a++) {
        const ang = (a / A) * Math.PI * 2;
        const x = Math.cos(ang) * rr, z = Math.sin(ang) * rr;
        verts.push(x, groundHeight(x, z), z);
      }
    }
    const idx = [];
    for (let k = 0; k < RINGS; k++) {
      for (let a = 0; a < A; a++) {
        const i0 = k * A + a, i1 = k * A + ((a + 1) % A), i2 = (k + 1) * A + a, i3 = (k + 1) * A + ((a + 1) % A);
        idx.push(i0, i1, i2, i1, i3, i2); // counter-clockwise seen from above → faces (and normals) point up
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    g.setIndex(idx);
    g.computeVertexNormals();

    const pos = g.attributes.position, nrm = g.attributes.normal;
    const colors = new Float32Array(pos.count * 3);
    const c = new THREE.Color();
    const grass = [new THREE.Color('#3b6324'), new THREE.Color('#5d8530'), new THREE.Color('#2f5220')];
    const meadow = new THREE.Color('#9a9a45'), bloom = new THREE.Color('#c9b85a');
    const forest = new THREE.Color('#22401f'), scree = new THREE.Color('#7d776c');
    const rockA = new THREE.Color('#5e5850'), rockB = new THREE.Color('#857c70'), rockC = new THREE.Color('#4a4642');
    const snow = new THREE.Color('#f3f5f8'), sand = new THREE.Color('#b9a77c');
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), h = pos.getY(i), z = pos.getZ(i);
      const up = nrm.getY(i);                         // 1 = flat, lower = steeper
      const r = Math.hypot(x, z);
      // meadows
      const v = 0.5 + 0.5 * fbm(x / 25, z / 25, 3);
      c.copy(grass[0]).lerp(grass[1], v).lerp(grass[2], Math.max(0, fbm(x / 60 + 9, z / 60, 2)));
      c.lerp(meadow, Math.max(0, fbm(x / 35 + 3, z / 35 - 5, 3)) * 0.55);
      c.lerp(bloom, Math.max(0, fbm(x / 9 - 2, z / 9 + 4, 2) - 0.25) * 0.5);
      // dark conifer forest on the lower mountain slopes
      c.lerp(forest, smooth(245, 290, r) * (1 - smooth(70, 110, h)) * smooth(0.55, 0.8, up) * 0.9);
      // rock: steep faces and high ground, with horizontal strata
      const strata = 0.5 + 0.5 * Math.sin(h * 0.45 + fbm(x / 30, z / 30, 2) * 3);
      const rock = rockA.clone().lerp(rockB, strata).lerp(rockC, Math.max(0, fbm(x / 12, z / 12, 2)));
      const rockAmount = Math.max(smooth(0.82, 0.6, up) * smooth(25, 60, h), smooth(85, 120, h));
      c.lerp(rock, rockAmount);
      c.lerp(scree, smooth(0.75, 0.9, up) * smooth(90, 120, h) * 0.5);
      // snow: settles on gentler high ground, slides off cliffs; ragged line from noise
      const snowLine = 128 + fbm(x / 40, z / 40, 3) * 22;
      c.lerp(snow, smooth(snowLine, snowLine + 18, h) * smooth(0.45, 0.72, up));
      // lake shore
      const dl = Math.hypot(x - LAKE.x, z - LAKE.z);
      c.lerp(sand, (1 - smooth(LAKE.r * 0.95, LAKE.r * 1.25, dl)) * 0.8);
      c.multiplyScalar((0.88 + 0.24 * (0.5 + 0.5 * fbm(x / 4, z / 4, 2))) * 0.8);
      colors.set([c.r, c.g, c.b], i * 3);
    }
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const uvs = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) { uvs[i * 2] = pos.getX(i) / 7; uvs[i * 2 + 1] = pos.getZ(i) / 7; }
    g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    return g;
  }, []);
  const detail = useMemo(makeGroundDetailTexture, []);
  const material = useMemo(() => addWeather(new THREE.MeshStandardMaterial({ vertexColors: true, map: detail, roughness: 0.95, envMapIntensity: 0.35 }), uniforms), [uniforms, detail]);
  useEffect(() => () => { geometry.dispose(); material.dispose(); detail.dispose(); }, [geometry, material, detail]);
  return <mesh geometry={geometry} material={material} position={[0, -0.02, 0]} />;
};

const Lake = ({ weatherRef }) => {
  const matRef = useRef();
  useFrame(() => {
    const w = weatherRef.current;
    if (!matRef.current) return;
    matRef.current.roughness = 0.06 + w.rain * 0.3 + w.wind * 0.15; // ripples
    matRef.current.color.set('#284a5c').lerp(new THREE.Color('#dfe6ec'), w.snow * 0.7); // frozen look in snow
  });
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[LAKE.x, -0.7, LAKE.z]}>
      <circleGeometry args={[LAKE.r * 1.15, 64]} />
      <meshStandardMaterial ref={matRef} color="#284a5c" metalness={0.65} roughness={0.06} />
    </mesh>
  );
};

// Realistic trees: pine, fir, oak and birch pictures (baked from 3D trees) on crossed billboards (6 triangles each),
// grouped into natural forest patches. Cheaper than geometric trees, so there can be more of them.
const Trees = ({ uniforms }) => {
  const total = IS_MOBILE ? 520 : 1100;
  const refs = [useRef(), useRef(), useRef(), useRef()];
  // Pre-baked tree pictures (see scripts/bake-trees.js); phones get a half-size atlas.
  const atlas = useTexture(IS_MOBILE ? '/textures/trees3d_atlas_mobile.webp' : '/textures/trees3d_atlas.webp');
  useMemo(() => { atlas.colorSpace = THREE.SRGBColorSpace; atlas.anisotropy = 4; }, [atlas]);
  const geometries = useMemo(() => TREE_KINDS.map((_, i) => makeBillboardGeometry(i)), []);
  const material = useMemo(() => {
    const m = new THREE.MeshStandardMaterial({ map: atlas, alphaTest: 0.45, side: THREE.DoubleSide, roughness: 0.9, envMapIntensity: 0.3 });
    m.alphaToCoverage = true; // smooth leafy edges with MSAA
    return addWeather(m, uniforms, { sway: true });
  }, [atlas, uniforms]);

  // Place all trees once; decide each tree's kind from altitude and a little randomness.
  const placements = useMemo(() => {
    let seed = 7;
    const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    const out = [[], [], [], []];
    let tries = 0;
    while (out.reduce((n, l) => n + l.length, 0) < total && tries < total * 12) {
      tries++;
      const a = rand() * Math.PI * 2, r = 68 + Math.pow(rand(), 0.85) * 190;
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (Math.hypot(x - LAKE.x, z - LAKE.z) < LAKE.r * 1.25) continue;
      const density = 0.5 + 0.5 * fbm(x / 70 + 4, z / 70 - 9, 3);       // forest patches and clearings
      if (rand() > Math.pow(density, 2.2) * 1.6) continue;
      const h = groundHeight(x, z);
      if (h > 105) continue;                                              // tree line
      const coniferChance = 0.35 + smooth(15, 70, h) * 0.6;
      const kind = rand() < coniferChance ? (rand() < 0.6 ? 0 : 1) : (rand() < 0.65 ? 2 : 3);
      out[kind].push({ x, y: h - 0.3, z, s: 0.7 + rand() * 0.65, rot: rand() * Math.PI, tint: 0.82 + rand() * 0.3 });
    }
    return out;
  }, [total]);

  useEffect(() => {
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), sc = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0), col = new THREE.Color();
    placements.forEach((list, k) => {
      const mesh = refs[k].current;
      if (!mesh) return;
      list.forEach((t, i) => {
        p.set(t.x, t.y, t.z);
        q.setFromAxisAngle(up, t.rot);
        sc.set(t.s, t.s * (0.9 + (t.tint - 0.82)), t.s);
        m.compose(p, q, sc);
        mesh.setMatrixAt(i, m);
        mesh.setColorAt(i, col.setScalar(t.tint));
      });
      mesh.count = list.length;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    });
  }, [placements]); // eslint-disable-line

  useEffect(() => () => {
    geometries.forEach((g) => g.dispose());
    material.dispose();
  }, [geometries, material]);

  return geometries.map((g, k) => (
    <instancedMesh key={k} ref={refs[k]} args={[g, material, Math.max(1, placements[k].length)]} frustumCulled={false} />
  ));
};

export const Landscape = ({ weatherRef }) => {
  const uniforms = useMemo(weatherUniforms, []);
  // Snow builds up and melts slowly; wetness follows the rain a little faster.
  const cover = useRef({ snow: 0, wet: 0 });
  useFrame((state, delta) => {
    const w = weatherRef.current;
    const c = cover.current;
    const dt = Math.min(delta, 0.1);
    c.snow += ((w.snow || 0) - c.snow) * dt * 0.12;
    c.wet += (Math.max(w.rain || 0, (w.snow || 0) * 0.3) - c.wet) * dt * 0.4;
    uniforms.uSnow.value = c.snow;
    uniforms.uWet.value = c.wet;
    uniforms.uWind.value = w.wind || 0;
    if (w.windX !== undefined) uniforms.uWindDir.value.set(w.windX, w.windZ);
    uniforms.uTime.value = state.clock.elapsedTime;
  });
  return (
    <group>
      <Terrain uniforms={uniforms} />
      <Lake weatherRef={weatherRef} />
      <Trees uniforms={uniforms} />
    </group>
  );
};

export default Landscape;
